import { useCubeQuery } from "@cubejs-client/react";
import * as Sentry from "@sentry/browser";

import { validateSchema } from "app/utils/validateSchema";

import type { Query } from "@cubejs-client/core";
import type { UseCubeQueryOptions } from "@cubejs-client/react";
import type { ZodIssue, ZodSchema } from "zod";

type UseValidatedCubeQueryResponse<T> = ReturnType<typeof useCubeQuery> & {
  validatedRawData: T | null; // T represents the inferred type from the Zod schema
  validationError: ZodIssue[] | null;
};

/**
 * Returns validated results from useCubeQuery
 * @param query - The Cube.js query object
 * @param schema - The Zod schema to validate the data against
 * @param options - Optional Cube.js query options
 * @returns Validated raw data, validation error, and the standard useCubeQuery response
 */
export function useValidatedCubeQuery<T>(
  query: Query,
  schema: ZodSchema<T>,
  options?: UseCubeQueryOptions,
): UseValidatedCubeQueryResponse<T> {
  const cubeQueryResponse = useCubeQuery(query, options);

  const [validatedRawData, validationError] = validateSchema(
    schema,
    cubeQueryResponse.resultSet?.rawData(),
  );

  // Capture and log validation errors
  if (validationError) {
    Sentry.captureException(validationError);
    console.error("Error validating data", validationError);
  }

  // Capture and log Cube.js query errors
  if (cubeQueryResponse.error) {
    Sentry.captureException(cubeQueryResponse.error);
    console.error(cubeQueryResponse.error.toString());
  }

  return {
    ...cubeQueryResponse,
    validatedRawData,
    validationError,
  };
}
